import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Overview } from "./components/Types/OperationsSite";
import { AppDispatch } from "./store";
import { fetchRefreshToken } from "./store/thunk/LoginThunk";
import { fetchUserPreferences } from "./store/thunk/OperationsOverviewThunk";
import { getValuesFromQueryString } from "./utils/CommonUtils";
import { startTransition } from "react";
 
 
const Analytics = lazy(() => import("./components/Analytics"));
const Reports = lazy(() => import("./components/Analytics/reports"));
const Customers = lazy(() => import("./components/Customers"));
const CreateEditViewSite = lazy(() => import("./components/Customers/components/sites/CreateEditViewSite"));
const CustomerAddEditIndex = lazy(() => import("./components/Customers/CustomerAddEditIndex"));
const CustomerDetails = lazy(() => import("./components/Customers/CustomerDetails"));
const EmissionOverview = lazy(() => import("./views/EmissionPerformance"));
const LoginScreen = lazy(() => import("./views/LoginScreen"));
const OperationsOverview = lazy(() => import("./views/OperationsOverview"));
const PageContainer = lazy(() => import("./views/PageContainer"));
const Sidebar = lazy(() => import("./views/Sidebar"));
interface PrivateRouteProps {
  element: JSX.Element;
} 
export default function App(): JSX.Element {
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [settingApiLoading, setSettingApiLoading] = useState<boolean>(true);
  const userPrefstate = useSelector((state: any) => state.userPreferenceState);
  const refreshTokenState = useSelector((state: any) => state.refreshTokenState);
  const [overviewTypeTab, setOverviewTypeTab] = useState<Overview>("operation");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(window.innerWidth >= 768);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [isInitializing, setIsInitializing] = useState(true);
 
  useEffect(() => {
    if (localStorage.getItem('sessionExpired') === 'true') {
      localStorage.removeItem('sessionExpired');
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.replace(process.env.REACT_APP_LOGOUT_URL as string);
      return;
    }
  }, [navigate, location]);
 
  useEffect(() => {
    const checkSessionExpiry = () => {
      if (localStorage.getItem('sessionExpired') === 'true') {
        localStorage.removeItem('sessionExpired');
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.replace(process.env.REACT_APP_LOGOUT_URL as string);
      }
    };
 
    const sessionCheckInterval = setInterval(checkSessionExpiry, 5000);
 
    return () => clearInterval(sessionCheckInterval);
  }, []);
 
  useEffect(() => {
    if (refreshTokenState?.data?.token?.refresh_token) {
      localStorage.setItem("access_token", refreshTokenState.data.token.access_token ?? "");
      localStorage.setItem("refresh_token", refreshTokenState.data.token.refresh_token ?? "");
    }
  }, [refreshTokenState, userPrefstate?.data]);
 
 
  useEffect(() => {
    const intervalId = setInterval(() => {
      const refreshToken = localStorage.getItem("refresh_token");
      const accessToken = localStorage.getItem("access_token");
      if (!refreshToken || !accessToken) {
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.setItem('sessionExpired', 'true');
        window.location.replace(process.env.REACT_APP_LOGOUT_URL as string);
        return;
      }
      dispatch(fetchRefreshToken({ refreshtoken: refreshToken, accessToken: accessToken }))
        .then((action: any) => {
          if (action && action.type && action.type.endsWith('rejected')) {
            localStorage.removeItem("isLoggedIn");
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.setItem('sessionExpired', 'true');
            window.location.replace(process.env.REACT_APP_LOGOUT_URL as string);
          }
        })
        .catch((error) => {
          localStorage.removeItem("isLoggedIn");
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          localStorage.setItem('sessionExpired', 'true');
          window.location.replace(process.env.REACT_APP_LOGOUT_URL as string);
        });
    }, 28 * 60 * 1000);
 
    return () => clearInterval(intervalId);
  }, [dispatch, navigate]);
 
  let settingAPILoading = userPrefstate.successful;
  useEffect(() => {
    if (settingAPILoading) setSettingApiLoading(!settingAPILoading);
  }, [settingAPILoading]);
 
useEffect(() => {
  const urlData = getValuesFromQueryString(search);
  setIsInitializing(false);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  const token = urlData.access_token || urlData.at;
  const refreshToken = urlData.refresh_token;

  if (token) {
    localStorage.setItem("access_token", token);
    localStorage.setItem("isLoggedIn", "true");
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    const cleanSearchParams = new URLSearchParams(search);
    cleanSearchParams.delete("access_token");
    cleanSearchParams.delete("at");
    cleanSearchParams.delete("refresh_token");

    const hasCustomerId = cleanSearchParams.get('CustomerId');
    const hasAssetId = cleanSearchParams.get('AssetId');
    let targetPath = '/operations-overview';
    if (hasCustomerId && hasAssetId) {
      targetPath = '/mapview';
    }
    startTransition(() => {
      navigate(`${targetPath}${cleanSearchParams.toString() ? `?${cleanSearchParams.toString()}` : ""}`, { replace: true });
    });
    return;
  }

  if (!isLoggedIn && location.pathname !== "/login") {
    navigate("/login");
  }

  setSettingApiLoading(false);
}, [navigate, location.pathname, search, location.pathname]);
 
 
 
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (isLoggedIn) {
      dispatch(fetchUserPreferences());
    }
  }, [dispatch]);
 
  const updateOverviewTypeTab = (newTab: any) => {
    setOverviewTypeTab(newTab);
  };
 
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    const hamburgerIcon = document.querySelector('.hamburger-icon');
    if (isSidebarOpen) {
      hamburgerIcon?.classList.add('active');
    } else {
      hamburgerIcon?.classList.remove('active');
    }
  };
 
  const closeSidebar = () => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };
 
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (window.innerWidth < 768 && sidebarRef.current && !sidebarRef.current.contains(event.target) && !event.target.classList.contains('hamburger-icon')) {
        setIsSidebarOpen(false);
      }
 
    };
 
    document.addEventListener('mousedown', handleClickOutside);
 
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarRef, setIsSidebarOpen]);
 
  if (isInitializing) {
    return <div>Loading...</div>; // or null
  }
  const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  return isLoggedIn ? element : <Navigate to="/login" replace />;
}
  const renderPage = () => {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/global" element={<Reports />} />
          <Route path="/customers" element={userPrefstate.data?.isCustomerAdmin ? <CustomerDetails /> : <Customers />} />
          <Route path="/CustomerDetails/:id/*" element={<CustomerDetails />} />
          <Route path="/add-customer" element={<CustomerAddEditIndex />} />
          <Route path="/edit-customer/:id" element={<CustomerAddEditIndex />} />
          <Route path="/create-site/:id" element={<CreateEditViewSite mode="create" />} />
          <Route path="/edit-site/:id" element={<CreateEditViewSite mode="edit" />} />
          <Route path="/view-site/:id" element={<CreateEditViewSite mode="view" />} />
          <Route path="/operations-overview/*" element={<OperationsOverview updateOverviewTypeTab={updateOverviewTypeTab} />} />
          <Route path="/emission-overview/*" element={<EmissionOverview updateOverviewTypeTab={updateOverviewTypeTab} />} />
          <Route path="/mapview/*" element={<PageContainer />} />
          <Route path="/" element={<PageContainer />} />
        </Routes>
      </Suspense>
    );
  };
 
  return (
    <>
      <div className="flex bg-cxLightGrey h-screen">
        {localStorage.getItem("isLoggedIn") === "true" && location.pathname !== "/login" && (
          <>
            <div
              id="sidebar"
              ref={sidebarRef}
              className={`sidebar ${isSidebarOpen ? "open" : "closed"} flex-shrink-0 flex-grow-0 basis-[4.17%] flex flex-col items-center z-10 p-2 h-full bg-cxSideBarBg`}
            >
              <Sidebar onMenuItemClick={closeSidebar} />
            </div>
            {isSidebarOpen && window.innerWidth < 768 && (
              <div className="sidebar-overlay active" onClick={closeSidebar}></div>
            )}
          </>
        )}
        <div className={`flex-grow main-content overflow-auto h-full`}>
          {renderPage()}
        </div>
        {window.innerWidth < 768 && (
          <div className={`hamburger-icon ${isSidebarOpen ? "hidden" : ""}`} onClick={toggleSidebar}>
            <svg width="15" height="15" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="30" height="4" fill="white" />
              <rect y="13" width="30" height="4" fill="white" />
              <rect y="26" width="30" height="4" fill="white" />
            </svg>
          </div>
        )}
      </div>
    </>
  );
}